import React, { useState, useEffect } from "react"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { useHistory } from "react-router-dom"
import { URLS } from "../../Url"
import axios from "axios"
import moment from "moment"
import {
  CardBody,
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Spinner,
} from "reactstrap"

function ViewOrder() {
  const navigate = useHistory()
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)

  const orderId = sessionStorage.getItem("OrderId")

  const getAuthToken = () => {
    try {
      const authUser = localStorage.getItem("authUser")
      if (!authUser) return null
      const tokenJson = JSON.parse(authUser)
      return tokenJson?.token || null
    } catch (error) {
      console.error("Error parsing auth token:", error)
      return null
    }
  }

  const token = getAuthToken()

  const getOrderDetails = async () => {
    if (!token) {
      toast.error("Authentication token not found")
      setLoading(false)
      return
    }

    if (!orderId) {
      toast.error("Order ID not found")
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const response = await axios.post(
        URLS.GetOneOrders,
        { orderId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      setOrder(response?.data?.data || null)
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch order data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getOrderDetails()
  }, [])

  const formatDate = dateString => {
    if (!dateString || !moment(dateString).isValid()) return "N/A"
    return moment(dateString).format("DD MMM YYYY, h:mm A")
  }

  const getStatusBadge = status => {
    const statusLower = status?.toLowerCase()
    const badgeStyles = {
      new: { color: "primary", label: "New" },
      processing: { color: "warning", label: "Processing" },
      completed: { color: "success", label: "Completed" },
      cancelled: { color: "danger", label: "Cancelled" },
      rejected: { color: "danger", label: "Rejected" },
      created: { color: "info", label: "New" },
      pending: { color: "warning", label: "Pending" },
      shipped: { color: "info", label: "Shipped" },
    }
    const { color, label } = badgeStyles[statusLower] || {
      color: "secondary",
      label: status || "Unknown",
    }
    return <b className={`text-${color} py-1 px-2`}>{label}</b>
  }

  const getPaymentStatusBadge = status => {
    const statusLower = status?.toLowerCase()
    const badgeStyles = {
      success: { color: "success", label: "Success" },
      failed: { color: "danger", label: "Failed" },
      pending: { color: "warning", label: "Pending" },
    }
    const { color, label } = badgeStyles[statusLower] || {
      color: "secondary",
      label: status || "Unknown",
    }
    return <b className={`text-${color} py-1 px-2`}>{label}</b>
  }

  const renderVariantDetails = product => {
    return product.variantNames.map((name, index) => (
      <div key={index} className="text-sm">
        <strong>{name}:</strong> {product.variantOptionNames[index]}
      </div>
    ))
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Order Details" breadcrumbItem="Order Information" />

        {loading ? (
          <Card>
            <CardBody className="text-center py-5">
              <Spinner color="primary" />
              <h4 className="mt-3">Loading order details...</h4>
            </CardBody>
          </Card>
        ) : order ? (
          <Row>
            <Col lg="12">
              <Card className="shadow-sm">
                <CardBody className="p-4">
                  <Row className="mb-2 align-items-center">
                    <Col md="4">
                      <h4 className="card-title mb-0">
                        Order #{order.orderUnquieId}
                      </h4>
                    </Col>
                    <Col md="8" className="text-md-end">
                      <div className="d-flex justify-content-md-end align-items-center gap-3">
                        <div>
                          <span className="text-muted me-2">Order Status:</span>
                          {getStatusBadge(order.status)}
                        </div>
                        <div>
                          <span className="text-muted me-2">
                            Payment Status:
                          </span>
                          {getPaymentStatusBadge(order.paymentStatus)}
                        </div>
                      </div>
                    </Col>{" "}
                  </Row>
                  <Row className="mb-2">
                    <Col md="12" className="d-flex justify-content-end gap-2">
                      <Button
                        onClick={() => navigate.goBack()}
                        style={{ float: "right" }}
                        color="primary"
                        size="sm"
                        aria-label="Go back to previous page"
                      >
                        <i className="far fa-arrow-alt-circle-left me-2"></i>
                        Back
                      </Button>
                    </Col>
                  </Row>

                  {order.cancelledReason && (
                    <Row className="mb-2">
                      <Col md="12">
                        <div className="alert alert-danger" role="alert">
                          <strong>Cancellation Reason:</strong>{" "}
                          {order.cancelledReason}
                        </div>
                      </Col>
                    </Row>
                  )}

                  <Row className="mb-2">
                    <Col md="6">
                      <div className="mb-2">
                        <h5 className="font-size-15 mb-2">Order Summary</h5>
                        <div className="table-responsive">
                          <Table className="table table-bordered mb-0">
                            <tbody>
                              <tr>
                                <th scope="row">Order ID</th>
                                <td>{order.orderUnquieId}</td>
                              </tr>
                              <tr>
                                <th scope="row">Order Date</th>
                                <td>{formatDate(order.logCreatedDate)}</td>
                              </tr>
                              <tr>
                                <th scope="row">Last Updated</th>
                                <td>{formatDate(order.logModifiedDate)}</td>
                              </tr>
                              <tr>
                                <th scope="row">Total Amount</th>
                                <td>₹{order.totalAmount.toFixed(2)}</td>
                              </tr>
                              <tr>
                                <th scope="row">Coupon Discount</th>
                                <td>
                                  ₹
                                  {parseFloat(
                                    order.couponDiscount || 0
                                  ).toFixed(2)}
                                </td>
                              </tr>
                              <tr>
                                <th scope="row">Product Discount</th>
                                <td>₹{order.productDiscount.toFixed(2)}</td>
                              </tr>
                              <tr>
                                <th scope="row">Payable Amount</th>
                                <td>₹{order.payableAmount.toFixed(2)}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-2">
                        <h5 className="font-size-15 mb-2">
                          Payment Information
                        </h5>
                        <div className="table-responsive">
                          <Table className="table table-bordered mb-0">
                            <tbody>
                              <tr>
                                <th scope="row">Payment Method</th>
                                <td className="text-capitalize">
                                  {order.paymentMode ||
                                    order.paymentMethod ||
                                    "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <th scope="row">Transaction ID</th>
                                <td>{order.transactionId || "N/A"}</td>
                              </tr>
                              <tr>
                                <th scope="row">Payment Amount</th>
                                <td>
                                  ₹
                                  {order.paymentAmount?.toFixed(2) ||
                                    order.payableAmount.toFixed(2)}
                                </td>
                              </tr>
                              <tr>
                                <th scope="row">Payment Currency</th>
                                <td>{order.paymentCurrency || "INR"}</td>
                              </tr>
                              <tr>
                                <th scope="row">Payment Status</th>
                                <td>
                                  {getPaymentStatusBadge(order.paymentStatus)}
                                </td>
                              </tr>
                              <tr>
                                <th scope="row">Paid</th>
                                <td>{order.isPaid ? "Yes" : "No"}</td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md="6">
                      <div className="mb-2">
                        <h5 className="font-size-15 mb-2">Store Information</h5>
                        <div className="d-flex align-items-start">
                          {order.storeProfile && (
                            <img
                              src={`${URLS.Base}${order.storeProfile}`}
                              alt={order.storeName}
                              className="rounded me-3"
                              height="60"
                              onError={e => {
                                e.target.onerror = null
                                e.target.src = "https://via.placeholder.com/60"
                              }}
                            />
                          )}
                          <div className="flex-grow-1">
                            <h6 className="mb-1">{order.storeName}</h6>
                            <p className="mb-1 text-muted">
                              {order.storeEmail}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-2">
                        <h5 className="font-size-15 mb-2">
                          Delivery Information
                        </h5>
                        <div className="table-responsive">
                          <Table className="table table-bordered mb-0">
                            <tbody>
                              <tr>
                                <th scope="row">Delivery Date</th>
                                <td>
                                  {formatDate(order.deliveryDate) ||
                                    "Not delivered yet"}
                                </td>
                              </tr>
                              <tr>
                                <th scope="row">Address</th>
                                <td>
                                  <div className="p-2">
                                    <h6 className="mb-1">
                                      {order.addressUserName}
                                    </h6>
                                    <p className="mb-1">
                                      {order.addressLine1}
                                      {order.addressLine2
                                        ? `, ${order.addressLine2}`
                                        : ""}
                                    </p>
                                    <p className="mb-1">
                                      {order.cityName}, {order.stateName},{" "}
                                      {order.countryName} - {order.pincode}
                                    </p>
                                    <p className="mb-0">
                                      <b className="text-primary">
                                        {order.addressType}
                                      </b>
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <div className="mb-2">
                    <h5 className="font-size-15 mb-2">Order Items</h5>
                    <div className="table-responsive">
                      <Table className="table table-bordered">
                        <thead>
                          <tr>
                            <th scope="col">Product</th>
                            <th scope="col">Variants</th>
                            <th scope="col">Quantity</th>
                            <th scope="col">Unit Price</th>
                            <th scope="col">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.orderProducts.map((product, index) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center">
                                  {product.productImages?.[0] && (
                                    <img
                                      src={`${URLS.Base}${product.productImages[0]}`}
                                      alt={product.productName}
                                      className="rounded me-3"
                                      height="60"
                                      onError={e => {
                                        e.target.onerror = null
                                        e.target.src =
                                          "https://via.placeholder.com/60"
                                      }}
                                    />
                                  )}
                                  <div>
                                    <h6 className="mb-0">
                                      {product.productName}
                                    </h6>
                                    <small className="text-muted">
                                      SKU: {product.productId.slice(-6)}
                                    </small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                {renderVariantDetails(product)}
                                {product.productVariantionPices?.length > 0 && (
                                  <div className="mt-2">
                                    <strong>Variant Prices:</strong>{" "}
                                    {product.productVariantionPices.map(
                                      (price, i) => (
                                        <span key={i}>
                                          ₹{price}
                                          {i <
                                          product.productVariantionPices
                                            .length -
                                            1
                                            ? ", "
                                            : ""}
                                        </span>
                                      )
                                    )}
                                  </div>
                                )}
                              </td>
                              <td>{product.quantity}</td>
                              <td>₹{parseFloat(product.price).toFixed(2)}</td>
                              <td>
                                ₹
                                {(
                                  parseFloat(product.price) *
                                  parseInt(product.quantity)
                                ).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan="4" className="text-end">
                              <strong>Grand Total:</strong>
                            </td>
                            <td>
                              <strong>₹{order.payableAmount.toFixed(2)}</strong>
                            </td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  </div>
                  <div className="mb-2">
                    <h5 className="font-size-15 mb-2">Order Status History</h5>
                    <div className="pl-6">
                      {order.orderStatus?.length > 0 ? (
                        <div className="d-flex items-start">
                          {order.orderStatus.map((status, index) => (
                            <div
                              key={index}
                              className="d-flex flex-column items-center relative"
                            >
                              {/* Timeline node */}
                              <div className="relative">
                                <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow z-10"></div>

                                {/* Connecting line (except for last item) */}
                                {index < order.orderStatus.length - 1 && (
                                  <div className="absolute top-1.5 left-full w-full h-0.5 bg-gray-300 -mr-4"></div>
                                )}
                              </div>

                              {/* Status content below the line */}
                              <div className="mt-2 text-center px-2">
                                <p className="font-semibold text-sm whitespace-nowrap">
                                  {getStatusBadge(status.status)}
                                </p>
                                <p className="text-muted text-xs whitespace-nowrap">
                                  {formatDate(status.logCreatedDate)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted">
                          No status history available
                        </p>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : (
          <Card>
            <CardBody className="text-center py-5">
              <h4>No order data found</h4>
              <p className="text-muted">
                Please check the order ID or try again later.
              </p>
            </CardBody>
          </Card>
        )}
      </Container>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  )
}

export default ViewOrder
